/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-shadow */
/* eslint-disable react/no-this-in-sfc */

function ImageZoomer(element = null, options = {}) {
  /* CONFIG */

  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
  const config = (({
    isDoubleTap = isTouch,
    template = '$pdp_1650x2199_hi_res$',
    zoomedInCallback = () => undefined,
    zoomedOutCallback = () => undefined,
    level = 3,
    ...rest
  }) => {
    let { classes = {} } = rest;

    /* PREDEFINED */
    const delta = 6;
    const doubleTapDelay = isTouch ? 500 : 300;

    /* CLASSES */

    const root = classes.root || 'image-zoomer';

    classes = {
      root,
      active: `${root}--active`,
      container: `${root}__container`,
      picture: `${root}__picture`,
      image: `${root}__image`,
      ...classes,
    };

    /* TEMPLATE */

    return {
      classes,
      template,
      delta,
      doubleTapDelay,
      zoomedInCallback,
      zoomedOutCallback,
      level,
      isDoubleTap,
      isTouch,
    };
  })(options);

  return {
    element,
    config,

    /* TAP */

    lastTap: 0,
    isNotDoubleTap: false,

    handleDoubleTap() {
      if (!this.lastTap) this.lastTap = new Date().getTime();

      const now = new Date().getTime();
      const timesince = now - this.lastTap;

      if (timesince < this.config.doubleTapDelay && timesince > 0) {
        this.lastTap = 0;
        return true;
      }

      this.lastTap = now;

      return false;
    },

    notDoubleTap() {
      return this.config.isDoubleTap && !this.handleDoubleTap();
    },

    /* CALCULATION */

    cursorPosition: {},
    imagePosition: {},

    getContainerSize() {
      const { level } = this.config;

      let { width, height } = this.element.getBoundingClientRect(); // Image

      width *= level;
      height *= level;

      return { width, height };
    },

    getCurrentPosition({ pageX, pageY, left, top }) {
      const x = left ? pageX - left : pageX;
      const y = top ? pageY - top : pageY;

      return { x, y };
    },

    getInitialImagePosition({ target, pageX, pageY }) {
      const { level } = this.config;

      const { left, top } = target.parentNode.getBoundingClientRect(); // Lens
      const { width, height } = this.container.getBoundingClientRect(); // Zoom container

      const dynamicWidth = width * level - width;
      const dynamicHeight = height * level - height;

      const topWithScroll = top + window.pageYOffset;

      let { x, y } = this.getCurrentPosition({ pageX, pageY, left, top: topWithScroll });

      x = -dynamicWidth * (x / width);
      y = -dynamicHeight * (y / height);

      this.imagePosition = { x, y };

      return { x, y };
    },

    getImagePosition({ currentTarget, target, pageX, pageY }) {
      const { left, top, height, width } = currentTarget.parentNode.getBoundingClientRect(); // Lens
      const { width: imageWidth, height: imageHeight } = target.getBoundingClientRect(); // Zoom image

      let { x, y } = this.getCurrentPosition({ pageX, pageY, left, top });

      const { x: lastX, y: lastY } = this.cursorPosition;
      const diffX = x - lastX;
      const diffY = y - lastY;

      this.cursorPosition = { x, y };

      const { x: lastImageX, y: lastImageY } = this.imagePosition;

      x = lastImageX + diffX;
      y = lastImageY + diffY;

      // Boundary
      if (x < width - imageWidth) x = width - imageWidth;
      if (x > 0) x = 0;
      if (y < height - imageHeight) y = height - imageHeight;
      if (y > 0) y = 0;

      this.imagePosition = { x, y };

      return { x, y };
    },

    /* PICTURE */

    getImageUrl(format) {
      return `${this.element.getAttribute('src').split('?')[0]}?${this.config.template}&fmt=${format}`;
    },

    getImageSources() {
      const imageFormats = ['webp', 'jpg'];

      return imageFormats
        .map(
          (format) => `
        <source type="image/${format}" srcSet="${this.getImageUrl(format)}" />
      `,
        )
        .join('');
    },

    getImageElement() {
      const { classes } = this.config;

      return `<img class="${classes.image}" src="${this.getImageUrl(
        'jpg',
      )}" width="1650" height="2199" alt="Zoomed In ${this.element.alt}" />`;
    },

    getPictureElement() {
      const { classes } = this.config;
      const pictureElement = document.createElement('picture');

      pictureElement.className = classes.picture;
      pictureElement.innerHTML = `
        ${this.getImageSources()}
        ${this.getImageElement()}
      `;

      return pictureElement;
    },

    /* CONTAINER */

    container: null,

    createContainer() {
      const { classes } = this.config;
      const container = document.createElement('div');
      const picture = this.getPictureElement();
      const { width, height } = this.getContainerSize();

      container.className = classes.container;
      container.style.cssText = `width: ${width}px; height: ${height}px;`;
      container.appendChild(picture);

      return container;
    },

    addContainer() {
      const container = this.createContainer();
      const imageParentElement = this.element.parentElement;

      imageParentElement.appendChild(container);
      imageParentElement.classList.add(`${this.config.classes.active}`);

      this.container = container;
    },

    removeContainer() {
      if (!this.container) return;

      const imageParentElement = this.element.parentElement;

      imageParentElement.removeChild(this.container);
      imageParentElement.classList.remove(`${this.config.classes.active}`);

      this.container = null; // Remove event listeners from garbage
    },

    /* EVENTS */

    isDown: false,
    isDragged: false,
    startPosition: null,
    isInitialEventAttached: false,

    touchHandler(event) {
      const mouseEventMap = {
        touchstart: 'mousedown',
        touchmove: 'mousemove',
        touchend: 'mouseup',
      };

      const { type } = event;
      const mouseEventName = mouseEventMap[`${type}`];

      if (!mouseEventName) return;
      if (mouseEventName === 'mousemove') event.stopPropagation();
      if (mouseEventName !== 'mousedown' && event.cancelable) event.preventDefault();

      const touches = event.changedTouches[0];
      const mouseEvent = new MouseEvent(mouseEventName, {
        bubbles: true,
        cancelable: true,
        view: window,
        screenX: touches.screenX,
        screenY: touches.screenY,
        clientX: touches.clientX,
        clientY: touches.clientY,
      });

      touches.target.dispatchEvent(mouseEvent);
    },

    clickEvent({ pageX, pageY }) {
      // Sometimes the mouseup event can occur first so we need to get the start position
      if (!this.startPosition) this.startPosition = { x: pageX, y: pageY };

      const { delta } = this.config;
      const { x, y } = this.startPosition;

      const diffX = Math.abs(pageX - x);
      const diffY = Math.abs(pageY - y);

      return diffX < delta && diffY < delta;
    },

    eventListenerMap({ element, eventMap = {}, isAdd = true }) {
      const listenerType = isAdd ? 'addEventListener' : 'removeEventListener';

      Object.keys(eventMap).forEach((key) => {
        const { callback = 'touchHandler', options = {}, cleanup = false } = eventMap[`${key}`];

        element[`${listenerType}`](key, this[`${callback}`], options);
        if (!isAdd && cleanup && callback !== 'touchHandler') this[`${callback}`] = null;
      });
    },

    setInitialEvent(isAdd = true) {
      const { element } = this;

      /* Event methods */

      if (isAdd) {
        this.initialMouseDownEvent = (event) => {
          this.isNotDoubleTap = this.notDoubleTap();
          this.startPosition = this.getCurrentPosition(event);
        };

        this.initialMouseUpEvent = (event) => {
          event.preventDefault();

          if (!this.clickEvent(event) || this.isNotDoubleTap) return;

          this.handleZoom(event);
          this.isNotDoubleTap = false;
        };
      }

      /* Event maps */

      const eventMap = {
        mousedown: {
          callback: 'initialMouseDownEvent',
          cleanup: true,
        },
        mouseup: {
          callback: 'initialMouseUpEvent',
          cleanup: true,
        },
      };

      this.eventListenerMap({ element, eventMap, isAdd });
      this.isInitialEventAttached = isAdd;
    },

    setEvents() {
      const {
        container: element,
        config: { isTouch },
      } = this;

      /* Event methods */

      this.mouseDownEvent = (event) => {
        if (!isTouch) event.preventDefault();

        this.isDown = true;

        const { pageX, pageY } = event;
        const { left, top } = this.element.getBoundingClientRect(); // Image

        this.cursorPosition = this.getCurrentPosition({ pageX, pageY, left, top });
        this.startPosition = this.getCurrentPosition({ pageX, pageY });
        this.isNotDoubleTap = this.notDoubleTap();
      };

      this.mouseMoveEvent = (event) => {
        if (!this.isDown) return;

        this.isDragged = true;

        event.stopPropagation();
        event.preventDefault();

        const imagePosition = this.getImagePosition(event, event.target);
        this.setZoomPosition(imagePosition);
      };

      this.mouseUpEvent = (event) => {
        if (this.clickEvent(event) && this.active && !this.isNotDoubleTap) this.handleZoom();
        this.resetEventState();
      };

      this.mouseOutEvent = () => this.resetEventState();

      /* Event maps */

      const eventMap = {
        mousedown: {
          callback: 'mouseDownEvent',
        },
        mousemove: {
          callback: 'mouseMoveEvent',
        },
        mouseup: {
          callback: 'mouseUpEvent',
        },
        ...((isTouch && {
          touchstart: {
            options: { passive: false },
          },
          touchmove: {
            options: { passive: false },
          },
          touchend: {
            options: { passive: false },
          },
        }) || {
          mouseout: {
            callback: 'mouseOutEvent',
          },
        }),
      };

      this.eventListenerMap({ element, eventMap });
    },

    resetEventState() {
      this.isDown = false;
      this.isDragged = false;
      this.isNotDoubleTap = false;
    },

    /* REFRESH - These can be used if the original reference is removed from the DOM */

    willRefreshElement() {
      return !document.body.contains(this.element);
    },

    refreshElement(element) {
      if (!element) return;
      this.element = element;
    },

    /* ZOOM */

    active: false,

    updateZoomLevel(level) {
      this.config.level = level;
    },

    setZoomPosition({ x, y }) {
      const { container, config } = this;
      container.querySelector(`.${config.classes.image}`).style.cssText = `left: ${x}px; top: ${y}px;`;
    },

    zoomIn(event) {
      const { zoomedInCallback } = this.config;

      this.addContainer();
      this.setZoomPosition(this.getInitialImagePosition(event));
      this.active = true;
      this.setEvents();
      zoomedInCallback();
    },

    zoomOut() {
      const { zoomedOutCallback } = this.config;

      this.removeContainer();
      this.active = false;
      zoomedOutCallback();
    },

    handleZoom(event) {
      if (this.active) {
        this.zoomOut();
        return;
      }

      this.zoomIn(event);
    },

    /* SETUP */

    toggleClass(isAdd = true) {
      const { element, config } = this;
      element.parentNode.classList[isAdd ? 'add' : 'remove'](config.classes.root);
    },

    enable() {
      if (this.isInitialEventAttached) return;
      this.toggleClass();
      this.setInitialEvent();
    },

    disable() {
      if (this.active) this.zoomOut();
      this.toggleClass(false); // False = Remove class
      this.setInitialEvent(false); // False = Remove events
    },
  };
}

ImageZoomer.create = function create(element, options) {
  const isImage = element instanceof HTMLImageElement;

  if (!isImage) throw new Error('The element is not an image');

  return this(element, options);
};

export default ImageZoomer;
